import { parse } from 'mathjs';
import { answerToleranceMode, answerSymmetry } from './answer-tolerance.js';
import { cleanComputed } from './epsilon.js';
import { evaluateFormulaRaw, preprocessAbsNotation } from './formula-scope.js';
import { planResolution } from './variables.js';
/**
 * Where a formula-driven answer's accepted band can sit across every roll a student might get,
 * and which variable values put it there.
 *
 * This lives in `packages/blocks` rather than in the numerical block's editor because it answers a
 * question about the DATA, not about a screen: a validation warning ("Default Incorrect can never
 * fire") and a preview control ("show me the lowest possible answer") both need it, and a copy per
 * caller is how the two drift apart.
 */
/** Cap on enumerated variable-value combinations. mathjs evaluates a moderately complex expression
 * in ~3 microseconds, so even 50,000 combinations (a couple of variables with dozens of steps each,
 * generously) stays well under 200ms. Pathological configs beyond this return null rather than
 * switching to a cheaper-but-unreliable method — a wrong envelope is worse than no envelope,
 * because every caller treats an envelope as exhaustive. */
export const FORMULA_SEARCH_MAX_COMBOS = 50000;
/**
 * Budget for branch-coverage enumeration of a real formula — a different workload from extrema
 * despite sharing the odometer, because coverage recomputes on every render of the editor while
 * the envelope does not.
 *
 * Only NON-affine formulas can reach this now. A bare variable takes the identity fast path at
 * COVERAGE_IDENTITY_MAX_VALUES, and anything linear in one variable is bounded from its endpoints
 * by `formulaBoundsCheap` without enumerating at all — so what is left is `{n}*{n}`, `sin({n})`,
 * `abs({n}-50)` and multi-variable formulas.
 *
 * Measured at this cap, worst case per pass including the coverage check: `{n}*{n}` 4.3ms,
 * `abs({n}-50)` 5.4ms, `sin({n})+{n}/3` 7.1ms. Under half a frame here, perhaps 20-55ms on a
 * battery device. 5,000 would put the worst case at 14ms, a whole frame spent on one part of one
 * block's validation before any device multiplier.
 *
 * Deliberately a fixed count rather than a time budget. Coverage gaps are errors and are intended
 * to gate publication, so their presence has to be deterministic — a wall-clock cap would let the
 * same task report a gap on a slow machine and not on a fast one, or differ between runs.
 */
export const COVERAGE_ENUMERATION_MAX_COMBOS = 2500;
/**
 * A far larger budget for the identity case — an equation that is exactly one `{variable}`.
 *
 * Measured: mathjs is ~98% of enumeration's cost (10,000 values takes 12.7ms to evaluate and
 * 0.21ms to generate). An identity formula does not need evaluating at all — the attainable set IS
 * the variable's domain — so the same 10,000 values cost about 1.4ms all-in including the coverage
 * check. That is the shape most setups have, since the branch equation is seeded as a bare
 * variable, so it is worth its own budget rather than being capped alongside real arithmetic.
 */
export const COVERAGE_IDENTITY_MAX_VALUES = 20000;
/**
 * Budget for quantifying feedback-overlap claims over the roll space.
 *
 * Sized like `COVERAGE_ENUMERATION_MAX_COMBOS` and for the same reason — this runs on every render
 * of the editor, not once — but the per-roll work is heavier: the accepted band is rebuilt and
 * EVERY value trigger is resolved against it, so the cost scales with the number of feedback states
 * as well as the number of rolls. A teacher with half a dozen triggers is the ordinary case, not
 * the pathological one.
 *
 * Over budget, the analysis makes no claim at all. That is the same direction every other search
 * here declines in: an overlap warning is an accusation, and a sampled accusation that appears and
 * vanishes with the dice is worse than silence.
 */
export const OVERLAP_ENUMERATION_MAX_COMBOS = 2500;
/** The variable an equation is exactly (`{n}` and nothing else), or null. */
function identityVariable(expr, variables) {
    const m = /^\s*\{([^}]+)\}\s*$/.exec(expr);
    if (!m)
        return null;
    return variables.find(v => v.name === m[1]) ?? null;
}
/**
 * How many combinations enumerating this formula would take, without doing it — so a caller can
 * tell "not variable-driven at all" (null) from "discrete but too big to check" (a number over the
 * budget). Those need different messages: the second must not fall back to real-line gap analysis,
 * which reports regions the variable cannot produce.
 */
export function formulaComboCount(formula, variables) {
    const expr = formula?.trim();
    if (!expr)
        return null;
    const names = [...new Set([...expr.matchAll(/\{([^}]+)\}/g)].map(m => m[1]))];
    const relevant = variables.filter(v => names.includes(v.name));
    if (relevant.length !== names.length || relevant.length === 0)
        return null;
    const domains = relevant.map(variableDomain);
    if (domains.some(d => d.length === 0))
        return null;
    return domains.reduce((acc, d) => acc * d.length, 1);
}
/**
 * Degree of an expression in one variable: 0 (constant), 1 (affine), or null (neither).
 *
 * A SYNTACTIC check on the parsed tree, not a sampled one. Sampling three points and testing for
 * linearity would pass a cubic that happens to be symmetric about its midpoint, and the cost of a
 * false positive is bounds narrower than the truth — which would report a reachable condition as
 * unreachable. Reading the structure cannot be fooled that way: multiplication is affine only when
 * one side is constant, division only by a constant, and any function call only over constants.
 */
function affineDegree(node, varName) {
    const n = node;
    switch (n.type) {
        case 'ConstantNode': return 0;
        // A symbol that is not the variable is a scope constant (pi, e). If it is neither, evaluation
        // returns NaN below and the caller declines, so treating it as constant here is safe.
        case 'SymbolNode': return n.name === varName ? 1 : 0;
        case 'ParenthesisNode': return n.content ? affineDegree(n.content, varName) : null;
        case 'OperatorNode': {
            const degs = (n.args ?? []).map(a => affineDegree(a, varName));
            if (degs.some(d => d === null))
                return null;
            const d = degs;
            switch (n.fn) {
                case 'unaryMinus':
                case 'unaryPlus': return d[0] ?? null;
                case 'add':
                case 'subtract': return Math.max(...d);
                // x*2 is affine; x*x is not.
                case 'multiply': {
                    const sum = d.reduce((a, b) => a + b, 0);
                    return sum <= 1 ? sum : null;
                }
                // 2/x is not affine, x/2 is.
                case 'divide': return d[1] === 0 ? d[0] : null;
                case 'pow': return d[0] === 0 && d[1] === 0 ? 0 : null;
                default: return null;
            }
        }
        case 'FunctionNode':
            return (n.args ?? []).every(a => affineDegree(a, varName) === 0) ? 0 : null;
        default: return null;
    }
}
/**
 * Bounds obtainable without enumerating the whole set.
 *
 * Two cases, both exact rather than approximate:
 *
 * - IDENTITY (`{n}`) — the bounds are the variable's own first and last domain values.
 * - AFFINE IN ONE VARIABLE (`2*{n}`, `{n}/3 + 1`, `-{n}`) — affine means monotonic, so the extremes
 *   sit at the domain's endpoints and two evaluations find them. That is the shape most branch
 *   equations actually take, and it is the difference between diagnosing `{test}*2` over 9,901
 *   values and saying nothing about it.
 *
 * Anything else returns null rather than a guess. Bounds are used to prove a condition
 * UNREACHABLE, so bounds narrower than the truth would produce a false accusation; declining is
 * the safe direction, and `branchesUnreachableInRange` never claims reachability from them either.
 */
export function formulaBoundsCheap(formula, variables, 
/**
 * The mode of the FIELD this formula came from.
 *
 * It matters even though only affine expressions get this far: `2*{n} + sin(30)` IS affine in
 * `n` — the trig term is a constant that does not involve it — so the evaluation below reads a
 * trig call either way, and reading it in the wrong mode shifts both bounds.
 */
angleMode = 'degrees') {
    const expr = formula?.trim();
    if (!expr)
        return null;
    const identity = identityVariable(expr, variables);
    if (identity) {
        const d = variableDomain(identity);
        return d.length > 0 ? { lo: d[0], hi: d[d.length - 1] } : null;
    }
    const names = [...new Set([...expr.matchAll(/\{([^}]+)\}/g)].map(m => m[1]))];
    if (names.length !== 1)
        return null;
    const v = variables.find(x => x.name === names[0]);
    if (!v)
        return null;
    const d = variableDomain(v);
    if (d.length === 0)
        return null;
    const bare = preprocessAbsNotation(expr.replace(/\{([^}]+)\}/g, '$1'));
    let deg;
    try {
        deg = affineDegree(parse(bare), v.name);
    }
    catch {
        return null;
    }
    if (deg === null)
        return null;
    const at = (x) => evaluateFormulaRaw(expr, { [v.name]: String(x) }, angleMode);
    const a = at(d[0]), b = at(d[d.length - 1]);
    if (!Number.isFinite(a) || !Number.isFinite(b))
        return null;
    return { lo: Math.min(a, b), hi: Math.max(a, b) };
}
// `evaluateFormulaRaw` now lives in formula-scope.ts, beside the scope it builds — moved to break
// a cycle, since `variables.ts` needs it and `forEachRoll` here needs `variables.ts`'s dependency
// ordering. Re-exported so every existing importer, including the app's formula-eval.ts, is
// untouched and there is still exactly ONE implementation.
export { evaluateFormulaRaw };
/** Every discrete value a random-mode variable can roll to — min/max/step define a finite,
 * enumerable set, not a continuum, which is what makes an exact envelope possible rather than an
 * endpoint guess. Derived variables (`formula` set) aren't enumerable, so they get an empty
 * domain and the whole search declines.
 *
 * `cleanComputed` on the walk, because these values are SHOWN. A step of 0.1 puts the fourth point
 * at 0.30000000000000004, and while a stray ULP never mattered to a comparison (`EPS`'s own note
 * measures this exact walk at 1 ULP), it goes on screen verbatim the moment Minimum or Maximum
 * hands its chosen assignment back to a chip. Random never showed it because `rollVariableValues`
 * formats what it picks; Min/Max returns the raw domain member. */
export function variableDomain(v) {
    if (v.formula)
        return [];
    // A constant is a domain of one. Not a special case to work around — it is what makes a
    // constant free to reference from a formula: the cross-product is multiplied by 1.
    if (v.value != null && !isNaN(v.value))
        return [cleanComputed(v.value)];
    if (v.min == null || v.max == null || v.step == null)
        return [];
    if (isNaN(v.min) || isNaN(v.max) || isNaN(v.step) || v.max <= v.min || v.step <= 0)
        return [];
    const count = Math.floor((v.max - v.min) / v.step);
    return Array.from({ length: count + 1 }, (_, i) => cleanComputed(v.min + i * v.step));
}
/** Unique `{var}` names appearing in any of these formulas, in first-seen order. */
export function chipNamesIn(formulas) {
    const order = [];
    const seen = new Set();
    for (const f of formulas) {
        const re = /\{([^}]+)\}/g;
        let m;
        while ((m = re.exec(f)) !== null) {
            if (!seen.has(m[1])) {
                seen.add(m[1]);
                order.push(m[1]);
            }
        }
    }
    return order;
}
/** The formula-bearing fields of the LIVE condition type. Only one of the three shapes is in force
 * at a time, and the other two keep whatever was last typed into them — so "every field" would
 * quantify over a formula the block is not currently grading against. */
export function bandFormulas(cfg) {
    if (cfg.conditionType === 'exact')
        return cfg.answers.map(a => a.value);
    if (cfg.conditionType === 'between')
        return cfg.betweenAnswers.flatMap(a => [a.low, a.high]);
    return [cfg.threshold];
}
/** Unique `{var}` names referenced across every formula-bearing field of a condition type. */
export function usedVariableNames(cfg) {
    return chipNamesIn(bandFormulas(cfg));
}
/**
 * What a sweep over these names actually has to enumerate.
 *
 * An EQUATION contributes its dependencies, not a dimension of its own — it is computed inside
 * each combination — so a question standing on twenty equations over two randoms still enumerates
 * two domains. A CONSTANT is a seed with a single value, which costs the cross-product nothing.
 *
 * `blocked` names what was asked for and cannot be enumerated at all: nothing defines it, or it
 * sits in a cycle. Callers differ on what to do about that — `forEachRoll` declines outright,
 * while the option checks let the option's own evaluation report it as unevaluable — so this
 * reports the fact rather than deciding.
 *
 * Three callers derived this separately before (`forEachRoll` here, and the option-formula and
 * option-chip collision checks), which is how one of them ends up enumerating what another
 * computes.
 */
export function seedsAndEquations(names, variables) {
    const byName = new Map(variables.map(v => [v.name, v]));
    const { ordered, unresolvable } = planResolution(variables);
    const unusable = new Set(unresolvable);
    const needed = new Set();
    const blocked = [];
    const stack = [...names];
    while (stack.length > 0) {
        const name = stack.pop();
        if (needed.has(name))
            continue;
        const v = byName.get(name);
        if (!v || unusable.has(name)) {
            blocked.push(name);
            continue;
        }
        needed.add(name);
        // `needed` guards the repeat visit, so a cycle among the formulas terminates here too.
        if (v.formula)
            stack.push(...chipNamesIn([v.formula]));
    }
    return {
        seeds: [...needed].map(name => byName.get(name)).filter(v => !v.formula),
        equations: ordered.filter(v => needed.has(v.name)),
        blocked,
    };
}
/**
 * Visit every roll in the cross-product of the named variables' domains.
 *
 * The odometer, in one place. It had been written out three times — the extrema search, the
 * attainable-set enumeration, and the option-collision sampler over in the app — and every caller
 * that wants to quantify a claim over "all rolls" needs exactly this loop behind exactly this
 * budget guard. A copy per caller is how one of them ends up SAMPLING where another enumerates,
 * which is the bug this was extracted to stop repeating.
 *
 * Returns false when the sweep could not be run safely and no caller should make any claim at all:
 * a name resolving to no enumerable variable (a typo, a deleted variable, a derived one), or more
 * combinations than the budget allows. False is not "no rolls" — it is "no answer", and keeping
 * those apart is the whole reason this returns a boolean rather than a count.
 *
 * `visit` may return 'stop' to abandon the sweep: the answer is already decided, or the formula
 * failed to resolve somewhere in the domain and the result is untrustworthy. Stopping does NOT make
 * this return false — the caller owns that verdict, because only the caller knows which of the two
 * it was.
 */
/*
 * THE ROLL SCOPE DOES NOT APPLY HERE — asked and answered, so it is written down.
 *
 * At runtime a task variable is drawn per task and an experience variable once per session
 * (`EXPERIENCE_ROLL_SCOPE`), so `{river}` is the same river in task 1 and task 5. It is a fair
 * question whether a sweep has to model that.
 *
 * It does not. A sweep quantifies over DOMAINS, and a variable's domain — `variableDomain`, the
 * grid `min + i*step` — is the same set whichever scope draws from it. The scope decides WHEN a
 * value is drawn and HOW WIDELY it is shared, never which values are possible. Callers are also
 * handed `variablesInForce(experienceVars, taskVars)`, both scopes merged, so nothing is missing
 * from the enumeration either.
 *
 * Sharing would only matter for a claim spanning two tasks — "this answer and that answer both
 * read `{river}`, so they agree". No caller makes one: every one of them asks about a single
 * block's formulas inside a single task, where one draw per name is what both the sweep and the
 * runtime give. The first caller that does make a cross-task claim has to revisit this, and that
 * is gated behind cross-task references, which are not built.
 */
export function forEachRoll(names, variables, visit, budget = FORMULA_SEARCH_MAX_COMBOS) {
    const unique = [...new Set(names)];
    if (unique.length === 0)
        return false;
    const { seeds, equations, blocked } = seedsAndEquations(unique, variables);
    // A name nothing defines, or one stuck in a cycle: no answer, which is not the same as no rolls.
    if (blocked.length > 0)
        return false;
    const domains = seeds.map(variableDomain);
    if (domains.some(d => d.length === 0))
        return false;
    const totalCombos = domains.reduce((acc, d) => acc * d.length, 1);
    if (totalCombos > budget)
        return false;
    const idx = new Array(domains.length).fill(0);
    for (let n = 0; n < totalCombos; n++) {
        const rolled = {};
        seeds.forEach((v, i) => { rolled[v.name] = String(domains[i][idx[i]]); });
        for (const eq of equations) {
            /*
             * EXACTLY WHAT `resolveVariables` DOES — `v.angleMode ?? 'degrees'` — because the sweep and
             * the student have to be quantifying over the same numbers.
             *
             * The mode belongs to the VARIABLE, not to whoever is sweeping: one equation resolving
             * differently depending on which block happened to ask would be the drift this file exists
             * to prevent. So there is nothing for a caller to pass, and nothing was ever passed.
             *
             * This used to DECLINE when a trig equation had no stored mode, to avoid guessing. It was
             * guarding a hazard that no longer exists: mathjs defaults to radians, which is why the
             * guard was written, but the resolver stopped deferring to that and now states degrees
             * outright. Declining did not make the sweep safer, it made it silent — and silent in the
             * common case, since the editor only WRITES `angleMode` when a teacher clicks the toggle,
             * so every untouched trig equation switched off coverage, overlap and Min/Max for its
             * whole block while the student's attempt computed it in degrees regardless.
             */
            const computed = evaluateFormulaRaw(eq.formula, rolled, eq.angleMode ?? 'degrees');
            // An equation that fails to resolve somewhere in the domain makes every claim over that
            // domain untrustworthy, so the whole sweep declines rather than skipping the combination.
            if (!isFinite(computed))
                return false;
            rolled[eq.name] = String(cleanComputed(computed));
        }
        if (visit(rolled) === 'stop')
            return true;
        for (let i = 0; i < idx.length; i++) {
            idx[i] = idx[i] + 1;
            if (idx[i] < domains[i].length)
                break;
            idx[i] = 0;
        }
    }
    return true;
}
/** The variables this condition's formula(s) reference, or null if any referenced name doesn't
 * resolve to an enumerable random-mode variable — a typo, a deleted variable, or a derived one. */
export function relevantVariablesForFormula(cfg, variables) {
    const usedNames = usedVariableNames(cfg);
    if (usedNames.length === 0)
        return null;
    const relevant = variables.filter(v => usedNames.includes(v.name));
    if (relevant.length !== usedNames.length)
        return null;
    if (relevant.some(v => variableDomain(v).length === 0))
        return null;
    return relevant;
}
/**
 * Every distinct value a single formula can produce across the full cross-product of its
 * variables' domains, sorted ascending. Null when no safe answer exists: an unresolvable or
 * derived variable, more combinations than the budget allows, or a formula that fails to resolve
 * for some combination.
 *
 * This is the ATTAINABLE SET, not the range, and for branch coverage the difference is the whole
 * point. `computeFormulaExtrema` answers "how far can this reach", which is right for asking
 * whether an accepted band escapes a slider. It cannot answer whether a specific value is
 * reachable: `{n}*2` over 1..10 spans 2 to 20, so `= 3` looks fine by extrema and can never
 * actually occur.
 *
 * Supersedes conditional-branch's `discreteEquationDomain`, which regex-matched a bare `{name}`
 * and declined everything else — a limitation of that implementation, not of the problem. A bare
 * variable is just the identity formula and falls out of this as a special case, and a formula
 * referencing no variables at all is a constant with exactly one attainable value.
 */
export function enumerateFormulaValues(formula, variables, angleMode = 'degrees', maxCombos = COVERAGE_ENUMERATION_MAX_COMBOS) {
    const expr = formula?.trim();
    if (!expr)
        return null;
    const usedNames = [];
    const seen = new Set();
    const re = /\{([^}]+)\}/g;
    let m;
    while ((m = re.exec(expr)) !== null) {
        if (!seen.has(m[1])) {
            seen.add(m[1]);
            usedNames.push(m[1]);
        }
    }
    // Identity fast path: the attainable set IS the variable's domain, so skip mathjs entirely and
    // take the much larger budget. Measured at ~98% of the cost. See COVERAGE_IDENTITY_MAX_VALUES.
    const identity = identityVariable(expr, variables);
    if (identity) {
        const d = variableDomain(identity);
        return d.length > 0 && d.length <= COVERAGE_IDENTITY_MAX_VALUES ? d : null;
    }
    const relevant = variables.filter(v => usedNames.includes(v.name));
    if (relevant.length !== usedNames.length)
        return null;
    if (relevant.some(v => variableDomain(v).length === 0))
        return null;
    const domains = relevant.map(variableDomain);
    const totalCombos = domains.reduce((acc, d) => acc * d.length, 1);
    if (totalCombos > maxCombos)
        return null;
    const values = new Set();
    const idx = new Array(domains.length).fill(0);
    for (let n = 0; n < totalCombos; n++) {
        const rolled = {};
        relevant.forEach((v, i) => { rolled[v.name] = String(domains[i][idx[i]]); });
        const value = evaluateFormulaRaw(expr, rolled, angleMode);
        // A domain point the formula cannot resolve (a division by zero inside the range) makes the
        // whole set untrustworthy — declining beats reporting a set with a hole in it as complete.
        if (!Number.isFinite(value))
            return null;
        values.add(value);
        for (let i = 0; i < idx.length; i++) {
            idx[i]++;
            if (idx[i] < domains[i].length)
                break;
            idx[i] = 0;
        }
    }
    return [...values].sort((a, b) => a - b);
}
/**
 * The edges of the accepted band, minimised and maximised over every possible roll.
 *
 * Exhaustive enumeration over each variable's real discrete domain, NOT a corner-only guess — a
 * corner check silently breaks for any non-monotonic formula (a squared term, an interior
 * extremum), and those are exactly the formulas a teacher writes without expecting trouble.
 *
 * What counts as an "edge" depends on the condition, and the difference matters:
 *
 * - `exact` — the tolerance band around each answer, so `lo`/`hi` are the outermost points the
 *   band ever covers.
 * - `between` — the authored low and high bounds.
 * - `lt`/`lte`/`gt`/`gte` — the THRESHOLD itself. The accepted set here is unbounded on one side,
 *   so its own min/max would be ±Infinity and tell a caller nothing. What actually varies with the
 *   roll is where the boundary sits, and that is what both callers want: the reachability check
 *   needs the rolls that move the boundary furthest, and the preview wants to show a teacher the
 *   loosest and strictest versions of their own condition.
 *
 * Returns null when it cannot be computed safely rather than guessing: an unresolvable or derived
 * variable, too many combinations to enumerate, or a formula that fails to resolve for some
 * combination (a domain that includes a division by zero, say — the failure is real, not a
 * rounding artefact, and hiding it would validate against a band that doesn't exist).
 *
 * `fallbackFormulas` is for the case where the ANSWER holds no formula but something else in the
 * block does — a feedback trigger written as an equation. There is then no band to extremise, and
 * the honest reading of "Minimum" becomes the roll that puts a feedback boundary at its lowest.
 * Consulted only when the band has no variables of its own, so nothing that already had an
 * envelope gets a different one.
 *
 * Note what this is NOT: the corner guess of "every variable at its own minimum". For `{a}/{b}` the
 * minimum is a's floor over b's CEILING, and no arrangement of per-variable corners finds that.
 * Exhaustive enumeration is what makes the answer right for a quotient, a square, or any interior
 * extremum, and it is why the budget exists rather than a cheaper method.
 */
export function computeFormulaExtrema(cfg, variables, fallbackFormulas = []) {
    const bandNames = usedVariableNames(cfg);
    // ONE-DIRECTIONAL, deliberately. Whenever the band itself holds a formula it IS the quantity, so
    // this behaves exactly as it always has; the fallback only fills a case that used to be simply
    // unavailable. Blending the two — extremising the band AND the triggers together — would silently
    // change what "Minimum" means for every block that already has a formula answer, and would hand
    // the reachability claims an envelope that is not the band's.
    const overBand = bandNames.length > 0;
    const names = overBand ? bandNames : chipNamesIn(fallbackFormulas);
    const edgesAt = overBand
        ? (rolled) => bandEdgesAt(cfg, rolled)
        : (rolled) => {
            const out = [];
            for (const f of fallbackFormulas) {
                const v = evaluateFormulaRaw(f, rolled, cfg.angleMode);
                if (!Number.isFinite(v))
                    return null;
                out.push(v);
            }
            return out.length > 0 ? out : null;
        };
    let lo = Infinity, hi = -Infinity;
    let loValues = {}, hiValues = {};
    let unresolvable = false;
    const swept = forEachRoll(names, variables, rolled => {
        const edges = edgesAt(rolled);
        if (edges === null) {
            unresolvable = true;
            return 'stop';
        }
        for (const e of edges) {
            if (e < lo) {
                lo = e;
                loValues = rolled;
            }
            if (e > hi) {
                hi = e;
                hiValues = rolled;
            }
        }
        return undefined;
    });
    if (!swept || unresolvable)
        return null;
    return Number.isFinite(lo) && Number.isFinite(hi) ? { lo, hi, loValues, hiValues } : null;
}
/** Every edge the accepted band contributes at ONE roll, or null if the roll cannot be resolved —
 * which makes the whole envelope untrustworthy, so the caller declines rather than skipping it. */
function bandEdgesAt(cfg, rolled) {
    const edges = [];
    if (cfg.conditionType === 'exact') {
        for (const a of cfg.answers) {
            const v = evaluateFormulaRaw(a.value, rolled, cfg.angleMode);
            if (!Number.isFinite(v))
                return null;
            const isAsym = answerSymmetry(a, cfg) === 'asymmetric';
            const below = parseFloat((isAsym ? a.toleranceBelow : a.toleranceValue) || '0');
            const above = parseFloat((isAsym ? a.toleranceAbove : a.toleranceValue) || '0');
            const mode = answerToleranceMode(a, cfg, 'absolute');
            if (!Number.isFinite(below) || !Number.isFinite(above))
                return null;
            /**
             * `Math.abs`, because a tolerance is a DISTANCE and a percentage of a negative answer is
             * not. `log(10)-2` is -1, and 1% of -1 is -0.01, so the band came out
             * `lo: -0.99, hi: -1.01` — inverted, and `isWithinAcceptedRange` then rejected every value
             * including the exact answer. A teacher with a negative answer and percentage error had a
             * block that could not be answered correctly at all.
             *
             * Absolute mode is clamped the same way: a negative distance is meaningless whatever typed
             * it, and letting one through produces the same inverted interval.
             */
            const loErr = Math.abs(mode === 'absolute' ? below : v * (below / 100));
            const hiErr = Math.abs(mode === 'absolute' ? above : v * (above / 100));
            edges.push(v - loErr, v + hiErr);
        }
    }
    else if (cfg.conditionType === 'between') {
        for (const a of cfg.betweenAnswers) {
            const low = evaluateFormulaRaw(a.low, rolled, cfg.angleMode);
            const high = evaluateFormulaRaw(a.high, rolled, cfg.angleMode);
            if (!Number.isFinite(low) || !Number.isFinite(high))
                return null;
            // Not assumed ordered: a formula pair can invert for some rolls and not others, and that
            // is a real authoring hazard the envelope should still bracket rather than reject.
            edges.push(Math.min(low, high), Math.max(low, high));
        }
    }
    else {
        const th = evaluateFormulaRaw(cfg.threshold, rolled, cfg.angleMode);
        if (!Number.isFinite(th))
            return null;
        edges.push(th);
    }
    return edges.length > 0 ? edges : null;
}
/** Whether a field carries a variable chip, and so cannot be judged without a roll.
 *
 * Shared because both numerical and slider had their own copy of this regex (`hasFVar` and
 * `hasVarChip`), and it is load-bearing in the same way in both: a chip-bearing field is AUTHORED
 * even when it cannot currently resolve, so completeness checks must treat it as filled while
 * defect checks resolve it. Getting that backwards is what made a perfectly good formula answer
 * report "setup incomplete" on the unselected card, where there is no roll at all. */
export function hasVariableChip(v) {
    return /\{[^}]+\}/.test(v);
}
/** Whether a field quotes an earlier answer — `[ara_...]`, the token a recall saves as. */
export function hasRecallChip(v) {
    return /\[ara_[0-9a-zA-Z-]+\]/.test(v);
}
/**
 * Whether a field needs something supplied from outside before it can be judged.
 *
 * The question `hasVariableChip` was answering all along — its own doc says "cannot be judged
 * without a roll" — but a recalled answer satisfies that too, and did not match. Not folded
 * into `hasVariableChip` because forty-one call sites read that one and some genuinely mean
 * "names a VARIABLE": a sweep that widened them all would have changed which fields get
 * swept for a roll, which is a different claim.
 *
 * The cost of getting this backwards is recorded in `hasVariableChip`'s own comment: a
 * perfectly good formula answer reporting "setup incomplete" on the card. A recall-only
 * answer did exactly that — the answer bar read "No Valid Answer" over an equation that was
 * fine, because nothing here called it a formula.
 */
export function needsSuppliedValue(v) {
    return hasVariableChip(v) || hasRecallChip(v);
}
/** One value each variable can genuinely take — the smallest, arbitrarily, since any member of the
 * domain answers "can this expression resolve at all?".
 *
 * Used to judge whether a formula is BROKEN (a typo, a deleted variable, bad syntax) as opposed to
 * merely unresolved. Judging that against the live roll is wrong: display surfaces have no roll, so
 * every formula would resolve to NaN and be reported broken purely for being looked at from the
 * wrong place. */
export function probeRoll(variables) {
    const out = {};
    for (const v of variables) {
        const domain = variableDomain(v);
        if (domain.length > 0)
            out[v.name] = String(domain[0]);
    }
    return out;
}
/**
 * The character that separates a trigger's parts, and the one thing a trigger VALUE may not contain.
 *
 * A targeted trigger is stored as one delimited string — `range:10:20`, `exact:45`, `b:range:1:2` —
 * and parsed back by splitting on this character. An ANSWER has no such problem: its low and high
 * are separate fields with nothing to collide. A trigger's share one string.
 *
 * That only became reachable when trigger values started accepting formulas, because `:` is mathjs
 * RANGE syntax (`1:5` is [1,2,3,4,5]). Such an expression is already useless as an answer — it is
 * not a scalar, so `evaluateFormulaRaw` returns NaN and every block flags it — but the corruption
 * happens at PARSE time, before evaluation: `range:1:5:20` splits into lo=1, hi=5, silently becoming
 * a different trigger that the NaN guard never sees.
 *
 * So stripping costs nothing real. It forbids only expressions that already fail, plus indexing into
 * a range (`(1:5)[2]`, which does yield a scalar but is a baroque way of writing `2`).
 */
export const TRIGGER_DELIMITER = ':';
/** Removes the delimiter from a value about to be encoded into a trigger string. Applied at the
 * input (so the character never appears as the teacher types, exactly like the numeric filters) AND
 * at every encoder, which is the path every write actually shares. */
export function sanitizeTriggerValue(v) {
    return v.split(TRIGGER_DELIMITER).join('');
}
/**
 * Whether the variables in `names` can be swept exhaustively, and if not, why.
 *
 * `forEachRoll` returns a bare `false` for five different reasons, so a caller that wants to SAY
 * something about the failure cannot tell which happened. This decides it from domain sizes alone,
 * without enumerating anything.
 */
export function sweepFeasibility(names, variables, budget = FORMULA_SEARCH_MAX_COMBOS) {
    const unique = [...new Set(names)];
    if (unique.length === 0)
        return 'ok'; // nothing to sweep: literals decide it
    const relevant = variables.filter(v => unique.includes(v.name));
    if (relevant.length !== unique.length)
        return 'unresolvable';
    const domains = relevant.map(variableDomain);
    if (domains.some(d => d.length === 0))
        return 'unresolvable';
    return domains.reduce((acc, d) => acc * d.length, 1) > budget ? 'over-budget' : 'ok';
}
/**
 * Does `left` ever exceed `right`, and if so, always or only sometimes?
 *
 * The question behind every inversion check in the editor: a `between` answer whose bounds are
 * equations may resolve the wrong way round on some rolls and not others, and a slider's two
 * handles may be unsatisfiable on some rolls and not others. Answering it with ONE roll — which is
 * what every existing check did, whether by sampling the probe roll, by `num()`ing a formula to 0,
 * or by reading whatever scenario happened to be loaded — reports a roll-specific fact as an
 * absolute. That is what produced "Slider 1 must have a lower correct value than Slider 2" on a
 * block where that is true of roughly half the rolls.
 *
 * THREE LAYERS, cheapest first, and each is exact wherever it speaks:
 *
 *  1. **Disjoint sides.** If the two expressions share no variables they are independent, so
 *     `max(left - right) = max(left) - min(right)` and `min(left - right) = min(left) - max(right)`.
 *     Each side's own extrema cost |domain| evaluations rather than the product, which is what makes
 *     the common two-variable case decidable at all: two variables over 400 values each are 160,000
 *     joint combos, well past the 50,000 budget, but 800 evaluations when taken separately.
 *  2. **Shared variables.** Then the sides are correlated and independent extrema OVER-report:
 *     `{a}` against `{a}+10` has max(left)=400 > min(right)=11, suggesting inversion, while the
 *     difference is always -10 and it never inverts. So the union must be swept jointly — which is
 *     cheap precisely because it is one shared variable.
 *  3. **Neither.** `unknown`, and the caller says so rather than staying silent.
 *
 * `strict` picks the comparison: `>` for "left exceeds right" (an inverted range), `>=` where
 * touching already counts.
 */
export function compareExpressionsAcrossRolls(left, right, variables, angleMode = 'radians') {
    const leftNames = chipNamesIn([left]);
    const rightNames = chipNamesIn([right]);
    const at = (expr, rolled) => evaluateFormulaRaw(expr, rolled, angleMode);
    // No variables at all: one evaluation settles it.
    if (leftNames.length === 0 && rightNames.length === 0) {
        const l = at(left, {}), r = at(right, {});
        if (!Number.isFinite(l) || !Number.isFinite(r))
            return 'unknown';
        return l > r ? 'always' : 'never';
    }
    const shared = leftNames.some(n => rightNames.includes(n));
    if (!shared) {
        // Independent: each side's own envelope is enough, and exact.
        const lx = extremaOf(left, leftNames, variables, angleMode);
        const rx = extremaOf(right, rightNames, variables, angleMode);
        if (!lx || !rx)
            return 'unknown';
        if (lx.lo > rx.hi)
            return 'always'; // even the smallest left beats the largest right
        if (lx.hi <= rx.lo)
            return 'never'; // even the largest left cannot reach the smallest right
        return 'sometimes';
    }
    // Correlated: the DIFFERENCE has to be quantified, so sweep the union.
    const names = [...new Set([...leftNames, ...rightNames])];
    if (sweepFeasibility(names, variables) !== 'ok')
        return 'unknown';
    let everAbove = false, everNotAbove = false, bad = false;
    const swept = forEachRoll(names, variables, rolled => {
        const l = at(left, rolled), r = at(right, rolled);
        if (!Number.isFinite(l) || !Number.isFinite(r)) {
            bad = true;
            return 'stop';
        }
        if (l > r)
            everAbove = true;
        else
            everNotAbove = true;
        return undefined;
    });
    if (!swept || bad)
        return 'unknown';
    return everAbove ? (everNotAbove ? 'sometimes' : 'always') : 'never';
}
/** One expression's exact envelope. `formulaBoundsCheap` handles the single-variable affine case
 *  (`{n}`, `{n}*30`, `{a}/2+5`) in two evaluations, which is the overwhelmingly common authoring
 *  shape; anything else falls back to an exhaustive sweep of its own variables. */
function extremaOf(expr, names, variables, angleMode) {
    if (names.length === 0) {
        const v = evaluateFormulaRaw(expr, {}, angleMode);
        return Number.isFinite(v) ? { lo: v, hi: v } : null;
    }
    const cheap = formulaBoundsCheap(expr, variables);
    if (cheap)
        return cheap;
    if (sweepFeasibility(names, variables) !== 'ok')
        return null;
    let lo = Infinity, hi = -Infinity, bad = false;
    const swept = forEachRoll(names, variables, rolled => {
        const v = evaluateFormulaRaw(expr, rolled, angleMode);
        if (!Number.isFinite(v)) {
            bad = true;
            return 'stop';
        }
        if (v < lo)
            lo = v;
        if (v > hi)
            hi = v;
        return undefined;
    });
    if (!swept || bad || !Number.isFinite(lo) || !Number.isFinite(hi))
        return null;
    return { lo, hi };
}
export function equationEvaluability(formula, variables, angleMode = 'degrees') {
    const expr = formula?.trim();
    if (!expr)
        return 'ok';
    const names = chipNamesIn([expr]);
    let finite = 0;
    let broken = 0;
    // No dependencies at all: one evaluation settles it. `1/0` is this case, and `forEachRoll`
    // declines an empty name list rather than sweeping a single empty roll.
    if (names.length === 0) {
        return Number.isFinite(evaluateFormulaRaw(expr, {}, angleMode)) ? 'ok' : 'never';
    }
    const swept = forEachRoll(names, variables, rolled => {
        if (Number.isFinite(evaluateFormulaRaw(expr, rolled, angleMode)))
            finite++;
        else
            broken++;
    });
    if (!swept)
        return 'unknown';
    if (broken === 0)
        return 'ok';
    return finite === 0 ? 'never' : 'sometimes';
}
//# sourceMappingURL=formula-extrema.js.map